import { useEffect, useState } from 'react'
import { ScaleIcon, SCALE_FILLS } from './ScaleIcons'
import { MAG_LABELS, BANDS, bandOf } from '../lib/scoring'
import { reducedMotion } from '../lib/sound'

/** Live animated demos for the how-to cards — no screenshots, always matches the real UI. */

function useCycle(n: number, ms: number): number {
  const [i, setI] = useState(0)
  useEffect(() => {
    if (reducedMotion()) return
    const t = setInterval(() => setI((x) => (x + 1) % n), ms)
    return () => clearInterval(t)
  }, [n, ms])
  return i
}

/** Slide 0: the thesis — a number multiplying ×10 through the magnitudes. */
export function OrderDemo() {
  const STEPS = ['1', '10', '100', '1,000', '10,000', '100,000', '1 million', '1 billion', '1 trillion', '1 quadrillion', '1 quintillion']
  const i = useCycle(STEPS.length, 700)
  return (
    <div className="demo demo-col" style={{ gap: 4 }}>
      <div key={i} className="demo-pop demo-order">{STEPS[i]}</div>
      <div className="tiny">×10 · ×10 · ×10 …</div>
    </div>
  )
}

const QUESTIONS = [
  'How many ants are alive right now?',
  'How much does a cloud weigh, in kg?',
  'How many planes are in the sky right now?',
  'How many shipwrecks are on the ocean floor?',
]

export function QuestionDemo() {
  const i = useCycle(QUESTIONS.length, 2400)
  return (
    <div className="demo">
      <div key={i} className="demo-q">
        <div className="demo-q-text lime">{QUESTIONS[i]}</div>
      </div>
    </div>
  )
}

const SCALE_STOPS = [3, 8, 12, 15]
export function ScaleDemo() {
  const i = useCycle(SCALE_STOPS.length, 2000)
  const exp = SCALE_STOPS[i]
  return (
    <div className="demo demo-scale-col">
      <div className="demo-sand"><b className="lime">{MAG_LABELS[exp]}</b> grains of sand would fill…</div>
      <div className="demo-scale">
        <div key={exp} className="demo-pop" style={{ color: 'var(--pop)' }}><ScaleIcon exp={exp} size={72} /></div>
        <div key={'t' + exp} className="demo-pop">
          <div className="demo-mag">{SCALE_FILLS[exp]}</div>
        </div>
      </div>
    </div>
  )
}

/** Miniature power dial, needle turned continuously as if by hand. */
export function DialDemo() {
  const [pos, setPos] = useState(4)
  useEffect(() => {
    if (reducedMotion()) return
    let raf = 0
    const t0 = performance.now()
    const run = (now: number) => {
      // slow ping-pong sweep 1..17 with sinusoidal ease — reads as a hand turning the knob
      const t = (now - t0) / 6000
      setPos(9 + 9 * Math.sin(t * Math.PI * 2 - Math.PI / 2))
      raf = requestAnimationFrame(run)
    }
    raf = requestAnimationFrame(run)
    return () => cancelAnimationFrame(raf)
  }, [])
  const C = 70, R = 56
  const rad = (d: number) => (d * Math.PI) / 180
  const pt = (r: number, d: number) => [C + r * Math.cos(rad(d)), C + r * Math.sin(rad(d))] as const
  const arc = (r: number, a: number, b: number) => {
    const [x0, y0] = pt(r, a), [x1, y1] = pt(r, b)
    return `M ${x0} ${y0} A ${r} ${r} 0 ${b - a > 180 ? 1 : 0} 1 ${x1} ${y1}`
  }
  const shown = reducedMotion() ? 12 : pos
  const deg = 135 + shown * 15
  const [nx, ny] = pt(R - 14, deg)
  return (
    <div className="demo demo-col">
      <svg viewBox="0 0 140 140" width="128" height="128" aria-hidden>
        <path d={arc(R, 135, 405)} fill="none" stroke="var(--line)" strokeWidth="4" strokeLinecap="round" />
        <path d={arc(R, 135.01, Math.max(136, deg))} fill="none" stroke="#B4FF39" strokeWidth="4" strokeLinecap="round"
          style={{ filter: 'drop-shadow(0 0 6px rgba(180,255,57,0.6))' }} />
        {Array.from({ length: 19 }, (_, k) => {
          const [x0, y0] = pt(R - (k % 3 === 0 ? 9 : 5), 135 + k * 15); const [x1, y1] = pt(R - 1, 135 + k * 15)
          return <line key={k} x1={x0} y1={y0} x2={x1} y2={y1} stroke="rgba(242,244,255,0.35)" strokeWidth={k % 3 === 0 ? 2 : 1.2} />
        })}
        <line x1={C} y1={C} x2={nx} y2={ny} stroke="#F2F4FF" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={C} cy={C} r="26" fill="var(--bg-2)" stroke="var(--line)" />
        <text x={C} y={C + 5} textAnchor="middle" fill="#F2F4FF" fontSize="15" fontWeight="700" fontFamily="var(--font)">
          {MAG_LABELS[Math.round(shown)]}
        </text>
      </svg>
      <div className="demo-bands">
        {BANDS.map((b, k) => <span key={b.label} className={'demo-band' + (k === bandOf(Math.round(shown)) ? ' on' : '')}>{b.label}</span>)}
      </div>
    </div>
  )
}

export function DigitDemo() {
  const seq = [
    { chip: 0, digit: 4, label: '4 billion' },
    { chip: 1, digit: 4, label: '40 billion' },
    { chip: 2, digit: 4, label: '400 billion' },
    { chip: 1, digit: 8, label: '80 billion' },
  ]
  const i = useCycle(seq.length, 1500)
  const cur = seq[i]
  return (
    <div className="demo demo-col">
      <div className="demo-chips">
        {['1B', '10B', '100B'].map((l, k) => <span key={l} className={'demo-band wide' + (k === cur.chip ? ' on' : '')}>{l}</span>)}
      </div>
      <div className="demo-digits">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <span key={d} className={'demo-key' + (d === cur.digit ? ' on' : '')}>{d}</span>
        ))}
      </div>
      <div className="demo-mag center" key={i}>
        <span className="demo-pop" style={{ display: 'inline-block' }}>{cur.label}</span>
      </div>
    </div>
  )
}

export function RevealDemo() {
  const beat = useCycle(4, 1300) // 0 guess · 1 +truth · 2 +verdict · 3 hold
  const b = reducedMotion() ? 2 : Math.min(beat, 2)
  return (
    <div className="demo">
      <div className="center demo-stack">
        <div><div className="tiny">your guess</div><div className="demo-guess">4 billion</div></div>
        <div style={{ visibility: b >= 1 ? 'visible' : 'hidden' }} className={b === 1 ? 'demo-punch' : ''}>
          <div className="tiny">the truth</div><div className="demo-truth">20 quadrillion</div>
        </div>
        <div style={{ visibility: b >= 2 ? 'visible' : 'hidden', fontSize: 24 }} className={b === 2 ? 'demo-pop' : ''}>
          💀 <b>WAY OFF!</b>
        </div>
      </div>
    </div>
  )
}

/** Slide 6: the sign-in pitch. */
export function SignInDemo() {
  return (
    <div className="demo" style={{ fontSize: 44, gap: 14 }}>
      <span className="demo-pop">🔥</span><span className="demo-pop" style={{ animationDelay: '0.1s' }}>🏆</span><span className="demo-pop" style={{ animationDelay: '0.2s' }}>📊</span>
    </div>
  )
}

export const DEMOS = [OrderDemo, QuestionDemo, ScaleDemo, DialDemo, DigitDemo, RevealDemo, SignInDemo]
